import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Loader from '../shared/Loader';
import ZeroState from 'shared/ZeroState';
import { FontSizes, Heading, Copy } from '@hexure/ui';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px;
`;

const SubHeading = styled(Copy)`
  margin: 8px 0 24px;
`;

const SectionTitle = styled.h4`
  font-size: ${FontSizes.Medium};
  margin: 24px 0 8px;
  font-weight: 450;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-bottom: 32px;
  border: 1px solid #e5e5e5;
`;

const TableHeader = styled.th`
  text-align: center;
  padding: 12px;
  border: 1px solid #ddd;
  background-color: #f9f9f9;
  font-size: ${FontSizes.SMALL};
`;

const TableCell = styled.td`
  padding: 12px;
  border: 1px solid #ddd;
  font-size: ${FontSizes.DEFAULT};
  max-width: 50%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: center;
  vertical-align: center;
`;

const getRateInfo = item => {
  if (item.cap_rate != null) {
    return ['Cap Rate', (item.cap_rate * 100).toFixed(2) + '%'];
  }
  if (item.performance_trigger != null) {
    return ['Triggered Rate', (item.performance_trigger * 100).toFixed(2) + '%'];
  }
  if (item.participation_rate != null) {
    return ['Participation Rate', (item.participation_rate * 100).toFixed(2) + '%'];
  }
  if (item.spread_rate != null) {
    return ['Spread', (item.spread_rate * 100).toFixed(2) + '%'];
  }
  if (item.fixed_rate != null) {
    return ['Fixed Rate', (item.fixed_rate * 100).toFixed(2) + '%'];
  }
  return ['N/A', 'N/A'];
};

const RatesInfo = ({ data = [], loading }) => {
  const list = Array.isArray(data) ? data : [];
  const byIndex = list.reduce((acc, item) => {
    const idx = item.index_name || 'N/A';
    if (!acc[idx]) acc[idx] = [];
    acc[idx].push(item);
    return acc;
  }, {});

  const entries = Object.entries(byIndex);

  return loading ? (
    <Loader />
  ) : (
    <Container>
      <Heading type='tertiary' style={{ marginBottom: '15px' }}>
        Crediting Strategies
      </Heading>
      {entries.length > 0 ? (
        entries.map(([indexName, items]) => (
          <div key={indexName}>
            <SectionTitle>{indexName}</SectionTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Rate Type</TableHeader>
                  <TableHeader>Rate</TableHeader>
                  <TableHeader>Crediting Strategy</TableHeader>
                  <TableHeader>Duration</TableHeader>
                  <TableHeader>Description</TableHeader>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => {
                  const [rateType, rateValue] = getRateInfo(item);
                  return (
                    <tr key={i}>
                      <TableCell>{item.marketing_name || 'N/A'}</TableCell>
                      <TableCell>{rateType || 'N/A'}</TableCell>
                      <TableCell>{rateValue || 'N/A'}</TableCell>
                      <TableCell>{item.strategy_name || 'N/A'}</TableCell>
                      <TableCell>{item.crediting_frequency || 'N/A'}</TableCell>
                      <TableCell>
                        {item.description ? (
                          item.description.length > 60 ? (
                            <details style={{ cursor: 'pointer' }}>
                              <summary>{item.description.slice(0, 60) + '…'}</summary>
                              <p style={{ margin: '0.5em 0' }}>{item.description}</p>
                            </details>
                          ) : (
                            item.description
                          )
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ))
      ) : (
        <ZeroState icon='mdi-magnify' message='No indexed rates available' />
      )}
    </Container>
  );
};

RatesInfo.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      index_name: PropTypes.string.isRequired,
      marketing_name: PropTypes.string.isRequired,
      strategy_name: PropTypes.string.isRequired,
      cap_rate: PropTypes.number,
      performance_trigger: PropTypes.number,
      participation_rate: PropTypes.number,
      spread_rate: PropTypes.number,
      fixed_rate: PropTypes.number,
      crediting_frequency: PropTypes.string,
      description: PropTypes.string
    })
  ),
  loading: PropTypes.bool
};

export default RatesInfo;
